import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { makeComponentMetaSelector } from 'selectors/navigator'
import { useMakeSelector, useSelector } from 'hooks/redux'
import { FEATURE_COLLECTION_DETAILS, DESIGN, OBSOLETE } from 'utils/constants'
import { ACTIVATE } from 'constants/ui/editor'
import strings from 'utils/strings'
import { Tooltip } from 'components/patterns'
import { setShowDrawerMask } from 'actions/drawer'
import { setComponentMeta } from 'actions/navigator'
import {
  activateCollectionAction,
  obsoleteCollectionAction,
  setActivationValidationError,
} from 'actions/collection'
import {
  getIsStatusUpdating,
  getActivationValidationError,
  getCollectionStatus,
  getCollectionId,
} from 'selectors/collection'
// Bug 538201: getCanViewCollectionRevision intentionally removed — lifecycle status
// is display-only and should be visible to all users. Action buttons (Activate/Obsolete)
// are independently gated inside LifecycleStatusVisual via getCanActivateCollection /
// getCanObsoleteCollection.
import {
  DrawerCharCount,
  DrawerContentWrapper,
  DrawerEditField,
  DrawerEditInput,
  DrawerEditLabel,
  DrawerEditTextarea,
  DrawerHeaderWrapper,
  DrawerSection,
  DrawerSectionLabel,
  DrawerSectionValue,
  DrawerTitle,
  TitleFieldWrapper,
  TitleFieldLabel,
  TitleFieldInput,
  RequiredText,
} from './FeatureCollectionDetails.style'
import LifecycleStatusVisual from './LifecycleStatusVisual'
import ActivationConfirmationPanel from './ActivationConfirmationPanel'

const MAX_DESCRIPTION_LENGTH = 1000
const XMC_START_INDEX = 3

// Overlay states
const DEFAULT = 'default'

/**
 * Maps vehicleSubType to display text for "Vehicle Type" field.
 * 42x -> "Plus Series", 43x -> "X-Series"
 * vehicleSubType values:
 *   - 'CHECP' or '42N', '38N' -> 'Plus Series'
 *   - 'CHEC2' or '43N', '45X' -> 'X-Series'
 *   - 'BUS' -> 'Bus'
 */
const getVehicleTypeDisplay = (vehicleSubType) => {
  if (!vehicleSubType) return 'N/A'
  if (vehicleSubType.startsWith('42')) return 'Plus Series'
  if (vehicleSubType.startsWith('43')) return 'X-Series'
  if (['CHECP', 'CEEA+', '38N'].includes(vehicleSubType)) return 'Plus Series'
  if (vehicleSubType === 'CHEC2' || vehicleSubType === '45X') return 'X-Series'
  if (vehicleSubType === 'BUS') return 'Bus'
  return vehicleSubType
}

/**
 * Converts stored VarMinCompat format "0115|0115" to display format
 * "XMC 3: App_0115  |  XMC 4: App_0115"
 * Bug 479697 Issue 11: Only include modules that are actually in the collection
 */
const formatVarMinCompat = (value, collectionModuleNames) => {
  if (!value) return 'N/A'
  return (
    value
      .split('|')
      .map((v, i) => {
        const moduleNum = XMC_START_INDEX + i
        const moduleName = `XMC${moduleNum}`
        // If collectionModuleNames provided, only include modules present in collection
        if (collectionModuleNames && collectionModuleNames.length > 0) {
          const isPresent = collectionModuleNames.some(
            (name) => name === moduleName || name === `XMC ${moduleNum}`
          )
          if (!isPresent) return null
        }
        return `XMC ${moduleNum}: App_${v.trim()}`
      })
      .filter(Boolean)
      .join('  |  ') || 'N/A'
  )
}

/**
 * FeatureCollectionDetailsDrawer (PBI 150666 + Edit Mode)
 * Displays collection details in the right drawer including:
 * - Title
 * - Description (with border bottom per Figma)
 * - Feature Collection Lifecycle Status (visual component with icons)
 * - Lifecycle action buttons (if user has permissions)
 * - Edit mode fields (title, description, vehicle type, sales code, variants)
 *
 * Uses ActivationConfirmationPanel for Activate/Obsolete confirmation
 * Based on Figma design "Activation Confirmation" panel
 */
const FeatureCollectionDetailsDrawer = () => {
  const dispatch = useDispatch()
  const [overlayState, setOverlayState] = useState(DEFAULT)
  // Snapshot of the collection ID at the moment a confirmation panel opens.
  // Using the live reducer ID at confirm-time would allow a navigation race
  // where the user opens confirmation for collection A, navigates to collection B
  // (which updates liveCollectionId), and then confirms — dispatching against B.
  // Pinning to the ID that was current when the panel opened prevents this.
  const [confirmedCollectionId, setConfirmedCollectionId] = useState(null)
  const isStatusUpdating = useSelector(getIsStatusUpdating)

  const {
    title,
    description,
    mdlDbCd,
    varMinCompat,
    isEditing,
    status,
    collectionId,
    hasObsoleteFeatures,
    vehicleSubType,
    collectionModuleNames,
  } = useMakeSelector(makeComponentMetaSelector, {
    id: FEATURE_COLLECTION_DETAILS,
  })

  const activationValidationError = useSelector(getActivationValidationError)
  // Bug 505198: Read status directly from the collection reducer (source of truth)
  // instead of relying solely on stale component meta. After activation/obsolete
  // succeeds, the reducer updates but the navigator component meta does not.
  // Only use live status when the reducer's collection matches this drawer's
  // collectionId to avoid showing a previous collection's status during navigation.
  const liveCollectionStatus = useSelector(getCollectionStatus)
  const liveCollectionId = useSelector(getCollectionId)
  const isMatchingCollection =
    liveCollectionId && String(liveCollectionId) === String(collectionId)
  // Normalize to title-case to match constants (Design, Active, Obsolete).
  const normalizedLiveStatus =
    isMatchingCollection && liveCollectionStatus
      ? liveCollectionStatus.charAt(0).toUpperCase() +
        liveCollectionStatus.slice(1).toLowerCase()
      : null
  const currentStatus = normalizedLiveStatus || status || DESIGN

  const [editTitle, setEditTitle] = useState(title || '')
  const [editDescription, setEditDescription] = useState(description || '')

  // Bug 505201: Sync local edit fields when drawer meta title/description
  // change (e.g. after duplicate navigation refreshes the meta). Only sync
  // when NOT actively editing to avoid clobbering user's in-progress input.
  useEffect(() => {
    if (!isEditing) {
      setEditTitle(title || '')
      setEditDescription(description || '')
    }
  }, [title, description, isEditing])

  // PBI 478731: Propagate title/description edits to component meta
  // so the detail page can read them when committing changes
  useEffect(() => {
    if (isEditing) {
      dispatch(
        setComponentMeta(FEATURE_COLLECTION_DETAILS, {
          title,
          description,
          mdlDbCd,
          varMinCompat,
          isEditing,
          status,
          collectionId,
          hasObsoleteFeatures,
          vehicleSubType,
          collectionModuleNames,
          editedTitle: editTitle,
          editedDescription: editDescription,
        })
      )
    }
  }, [editTitle, editDescription]) // eslint-disable-line react-hooks/exhaustive-deps

  // PBI 150666: Show editable title field when there's a validation error for missing title
  const showEditableTitle = activationValidationError?.hasMissingTitle

  // PBI 150666: Check if there's an active validation error
  const hasValidationWarning = Boolean(
    activationValidationError?.hasObsoleteFeatures ||
      activationValidationError?.hasMissingTitle
  )

  // Show activate confirmation overlay (with validation for missing title and obsolete features)
  const handleActivateClick = useCallback(() => {
    // PBI 150666: Validate before activation - check for missing title first
    if (!title || title.trim() === '') {
      dispatch(
        setActivationValidationError({
          type: 'missing_title',
          hasMissingTitle: true,
        })
      )
      return
    }

    // PBI 150666: Validate before activation - check for obsolete features.
    // Bug 505198: Only apply the hasObsoleteFeatures block when componentMeta
    // belongs to the currently-loaded collection (isMatchingCollection). Without
    // this guard, stale componentMeta from a previously-visited collection (where
    // hasObsoleteFeatures was true) would silently block activation on a freshly
    // created collection even though the new collection has no obsolete features.
    // isMatchingCollection compares liveCollectionId (from the collection reducer)
    // against the collectionId stored in componentMeta.
    if (isMatchingCollection && hasObsoleteFeatures) {
      dispatch(
        setActivationValidationError({
          type: 'obsolete_features',
          hasObsoleteFeatures: true,
        })
      )
      return
    }

    // Snapshot the authoritative ID now so the confirm handler uses this
    // identity even if liveCollectionId changes due to navigation.
    setConfirmedCollectionId(liveCollectionId || collectionId)
    setOverlayState(ACTIVATE)
    dispatch(setShowDrawerMask(true))
  }, [
    dispatch,
    title,
    isMatchingCollection,
    hasObsoleteFeatures,
    liveCollectionId,
    collectionId,
  ])

  // Show obsolete confirmation overlay
  const handleObsoleteClick = useCallback(() => {
    // Snapshot the authoritative ID now so the confirm handler uses this
    // identity even if liveCollectionId changes due to navigation.
    setConfirmedCollectionId(liveCollectionId || collectionId)
    setOverlayState(OBSOLETE)
    dispatch(setShowDrawerMask(true))
  }, [dispatch, liveCollectionId, collectionId])

  // Cancel and return to default state
  const handleCancel = useCallback(() => {
    setOverlayState(DEFAULT)
    dispatch(setShowDrawerMask(false))
  }, [dispatch])

  // Confirm activate action
  // Bug 505198: Use confirmedCollectionId — the ID snapshotted when the panel
  // opened — rather than liveCollectionId at confirm-time. If the user navigated
  // to a different collection while the panel was open, liveCollectionId will
  // have changed to the new collection's ID; using it here would activate the
  // wrong collection. We dismiss instead if the live ID no longer matches.
  const handleActivateConfirm = useCallback(() => {
    if (!confirmedCollectionId) {
      setOverlayState(DEFAULT)
      dispatch(setShowDrawerMask(false))
      return
    }
    // Guard: if the live collection ID is absent (reset during navigation) or has
    // changed since the panel opened, the user navigated away — dismiss without
    // dispatching to avoid acting on the wrong collection. A null liveCollectionId
    // means resetCollection fired (navigation in progress) and must be treated as
    // a mismatch, not as "no collection loaded yet, proceed anyway".
    if (
      !liveCollectionId ||
      String(liveCollectionId) !== String(confirmedCollectionId)
    ) {
      setOverlayState(DEFAULT)
      dispatch(setShowDrawerMask(false))
      return
    }
    dispatch(activateCollectionAction({ id: confirmedCollectionId }))
    setOverlayState(DEFAULT)
    dispatch(setShowDrawerMask(false))
  }, [dispatch, confirmedCollectionId, liveCollectionId])

  // Confirm obsolete action — same snapshot + guard pattern as handleActivateConfirm
  const handleObsoleteConfirm = useCallback(() => {
    if (!confirmedCollectionId) {
      setOverlayState(DEFAULT)
      dispatch(setShowDrawerMask(false))
      return
    }
    // Guard: same null/mismatch protection as handleActivateConfirm.
    // A null liveCollectionId means navigation reset the collection —
    // dismiss without dispatching rather than obsoleting the wrong collection.
    if (
      !liveCollectionId ||
      String(liveCollectionId) !== String(confirmedCollectionId)
    ) {
      setOverlayState(DEFAULT)
      dispatch(setShowDrawerMask(false))
      return
    }
    dispatch(obsoleteCollectionAction({ id: confirmedCollectionId }))
    setOverlayState(DEFAULT)
    dispatch(setShowDrawerMask(false))
  }, [dispatch, confirmedCollectionId, liveCollectionId])

  const showOverlay = overlayState !== DEFAULT

  // Render confirmation panel based on overlay state
  const renderConfirmationPanel = () => {
    if (overlayState === ACTIVATE) {
      return (
        <ActivationConfirmationPanel
          header={strings.confirm_activate_collection_header}
          body={strings.confirm_activate_collection_body}
          confirmText={strings.activate}
          onConfirm={handleActivateConfirm}
          onCancel={handleCancel}
          isLoading={isStatusUpdating}
        />
      )
    }

    if (overlayState === OBSOLETE) {
      return (
        <ActivationConfirmationPanel
          header={strings.confirm_obsolete_collection_header}
          body={strings.confirm_obsolete_collection_body}
          confirmText={strings.make_obsolete}
          onConfirm={handleObsoleteConfirm}
          onCancel={handleCancel}
          isLoading={isStatusUpdating}
        />
      )
    }

    return null
  }

  // Bug 505198: The overlay check MUST come before the isEditing early-return.
  // Previously showOverlay was checked after the edit-mode block, making it
  // unreachable when the user clicked Activate/Obsolete from edit mode.
  // The result: overlayState was set to ACTIVATE and the drawer mask darkened,
  // but the edit-mode return fired first on re-render so the confirmation panel
  // was never shown and no API call was ever dispatched.
  if (showOverlay) {
    return <>{renderConfirmationPanel()}</>
  }

  // Bug 479602: Only Design collections can be edited
  // Defensive check: don't show edit mode for Active/Obsolete
  const isDesignStatus = currentStatus?.toLowerCase() === DESIGN.toLowerCase()

  // Edit mode drawer - only shown if status is Design
  if (isEditing && isDesignStatus) {
    return (
      <DrawerContentWrapper>
        <DrawerHeaderWrapper>
          <DrawerTitle>{title}</DrawerTitle>
          {/* Bug 479697: Title field with required asterisk per Figma */}
          <DrawerEditField style={{ marginTop: 16 }}>
            <DrawerEditLabel>
              * {strings.feature_collection_title}
            </DrawerEditLabel>
            <DrawerEditInput
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              maxLength={80}
            />
          </DrawerEditField>
        </DrawerHeaderWrapper>
        {/* Bug 479697: Description section with proper border styling */}
        <DrawerSection>
          <DrawerEditField>
            <DrawerEditLabel>
              {strings.feature_collection_description}
            </DrawerEditLabel>
            <DrawerEditTextarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION_LENGTH}
            />
          </DrawerEditField>
          <DrawerCharCount>
            {MAX_DESCRIPTION_LENGTH - editDescription.length}
          </DrawerCharCount>
        </DrawerSection>
        {/* Bug 479697: Lifecycle status section per Figma edit mode design */}
        {/* Bug 538201: No permission gate — see read-mode comment above */}
        <DrawerSection>
          <LifecycleStatusVisual
            status={currentStatus}
            collectionName={title}
            onActivateClick={handleActivateClick}
            onObsoleteClick={handleObsoleteClick}
            hasValidationWarning={hasValidationWarning}
          />
        </DrawerSection>
        {/* Bug 486891 Issues 2+3: Added tooltip icons for Vehicle Type and QuickFit Sales Code in edit mode */}
        <DrawerSection>
          <div>
            <DrawerSectionLabel>
              Vehicle Type:
              <Tooltip
                title={
                  strings.vehicle_type_tooltip ||
                  'Ensure this type matches the vehicle to avoid unexpected Feature behavior.'
                }
              >
                <span style={{ marginLeft: 4, cursor: 'default' }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.20572 8.22795H7.45488V7.97811C7.45488 7.82413 7.47845 7.69057 7.52559 7.57744C7.57273 7.46431 7.65443 7.35746 7.77071 7.2569C7.88698 7.1532 8.04882 7.04164 8.25623 6.92222C8.58934 6.72738 8.84074 6.50269 9.01044 6.24815C9.18013 5.99046 9.26498 5.68406 9.26498 5.32896V5.31953C9.26498 4.96128 9.17071 4.64545 8.98215 4.37205C8.79675 4.09865 8.53591 3.88496 8.19966 3.73098C7.86655 3.57699 7.47531 3.5 7.02593 3.5C6.54512 3.5 6.13502 3.58328 5.79562 3.74983C5.45937 3.91639 5.20011 4.14893 5.01785 4.44747C4.83872 4.74287 4.74444 5.08227 4.73502 5.46566V5.47508L6.00774 5.47037L6.01717 5.46566C6.0266 5.28967 6.07059 5.13726 6.14916 5.00842C6.22772 4.87643 6.33457 4.7743 6.4697 4.70202C6.60483 4.62974 6.76195 4.5936 6.94108 4.5936C7.1202 4.5936 7.27733 4.62817 7.41246 4.69731C7.54759 4.7633 7.65129 4.85443 7.72357 4.97071C7.79899 5.08384 7.8367 5.21425 7.8367 5.36195V5.37138C7.8367 5.51908 7.81313 5.64792 7.76599 5.75791C7.71885 5.8679 7.63715 5.97318 7.52087 6.07374C7.40774 6.1743 7.24905 6.28586 7.04478 6.40842C6.83109 6.53412 6.65668 6.66925 6.52155 6.8138C6.38956 6.95836 6.29686 7.12334 6.24343 7.30875C6.19315 7.49102 6.17744 7.70628 6.1963 7.95455L6.20572 8.22795ZM6.8798 10.5C7.14377 10.5 7.35118 10.4293 7.50202 10.2879C7.656 10.1465 7.733 9.95477 7.733 9.71279C7.733 9.46453 7.656 9.27127 7.50202 9.133C7.35118 8.99158 7.14377 8.92088 6.8798 8.92088C6.61582 8.92088 6.40685 8.99158 6.25286 9.133C6.09888 9.27127 6.02189 9.46453 6.02189 9.71279C6.02189 9.95477 6.09888 10.1465 6.25286 10.2879C6.40685 10.4293 6.61582 10.5 6.8798 10.5Z"
                      fill="#0B2E6B"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13ZM7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                      fill="#8C96A2"
                    />
                  </svg>
                </span>
              </Tooltip>
            </DrawerSectionLabel>
            <DrawerSectionValue>
              {getVehicleTypeDisplay(vehicleSubType)}
            </DrawerSectionValue>
          </div>
          <div>
            <DrawerSectionLabel>
              QuickFit Sales Code:
              <Tooltip
                title={
                  strings.quickfit_sales_code_tooltip ||
                  'Match this code to the vehicle to avoid unexpected Feature behavior.'
                }
              >
                <span style={{ marginLeft: 4, cursor: 'default' }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.20572 8.22795H7.45488V7.97811C7.45488 7.82413 7.47845 7.69057 7.52559 7.57744C7.57273 7.46431 7.65443 7.35746 7.77071 7.2569C7.88698 7.1532 8.04882 7.04164 8.25623 6.92222C8.58934 6.72738 8.84074 6.50269 9.01044 6.24815C9.18013 5.99046 9.26498 5.68406 9.26498 5.32896V5.31953C9.26498 4.96128 9.17071 4.64545 8.98215 4.37205C8.79675 4.09865 8.53591 3.88496 8.19966 3.73098C7.86655 3.57699 7.47531 3.5 7.02593 3.5C6.54512 3.5 6.13502 3.58328 5.79562 3.74983C5.45937 3.91639 5.20011 4.14893 5.01785 4.44747C4.83872 4.74287 4.74444 5.08227 4.73502 5.46566V5.47508L6.00774 5.47037L6.01717 5.46566C6.0266 5.28967 6.07059 5.13726 6.14916 5.00842C6.22772 4.87643 6.33457 4.7743 6.4697 4.70202C6.60483 4.62974 6.76195 4.5936 6.94108 4.5936C7.1202 4.5936 7.27733 4.62817 7.41246 4.69731C7.54759 4.7633 7.65129 4.85443 7.72357 4.97071C7.79899 5.08384 7.8367 5.21425 7.8367 5.36195V5.37138C7.8367 5.51908 7.81313 5.64792 7.76599 5.75791C7.71885 5.8679 7.63715 5.97318 7.52087 6.07374C7.40774 6.1743 7.24905 6.28586 7.04478 6.40842C6.83109 6.53412 6.65668 6.66925 6.52155 6.8138C6.38956 6.95836 6.29686 7.12334 6.24343 7.30875C6.19315 7.49102 6.17744 7.70628 6.1963 7.95455L6.20572 8.22795ZM6.8798 10.5C7.14377 10.5 7.35118 10.4293 7.50202 10.2879C7.656 10.1465 7.733 9.95477 7.733 9.71279C7.733 9.46453 7.656 9.27127 7.50202 9.133C7.35118 8.99158 7.14377 8.92088 6.8798 8.92088C6.61582 8.92088 6.40685 8.99158 6.25286 9.133C6.09888 9.27127 6.02189 9.46453 6.02189 9.71279C6.02189 9.95477 6.09888 10.1465 6.25286 10.2879C6.40685 10.4293 6.61582 10.5 6.8798 10.5Z"
                      fill="#0B2E6B"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13ZM7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                      fill="#8C96A2"
                    />
                  </svg>
                </span>
              </Tooltip>
            </DrawerSectionLabel>
            <DrawerSectionValue>{mdlDbCd || 'N/A'}</DrawerSectionValue>
          </div>
          <div>
            <DrawerSectionLabel>
              Minimum Diagnostic Variant(s):
            </DrawerSectionLabel>
            <DrawerSectionValue>
              {formatVarMinCompat(varMinCompat, collectionModuleNames)}
            </DrawerSectionValue>
          </div>
        </DrawerSection>
      </DrawerContentWrapper>
    )
  }

  // Default read-only drawer with lifecycle
  return (
    <DrawerContentWrapper>
      {/* Title Section - PBI 150666: Show editable field when missing title error */}
      <DrawerHeaderWrapper>
        {showEditableTitle ? (
          <TitleFieldWrapper>
            <TitleFieldLabel>
              {strings.feature_collection_title || 'Feature Collection Title'}
            </TitleFieldLabel>
            <TitleFieldInput
              type="text"
              placeholder={
                strings.collection_title_placeholder || 'Collection Title'
              }
              value={title || ''}
              hasError={activationValidationError?.hasMissingTitle}
              readOnly
            />
            {activationValidationError?.hasMissingTitle && (
              <RequiredText>{strings.required || 'Required'}</RequiredText>
            )}
          </TitleFieldWrapper>
        ) : (
          <DrawerTitle>{title}</DrawerTitle>
        )}
      </DrawerHeaderWrapper>

      {/* Description Section (with border bottom per Figma) */}
      {/* Bug 479697 Issue 16: Use same label/value styles as metadata for consistent spacing */}
      <DrawerSection>
        <div>
          <DrawerSectionLabel>{strings.description}:</DrawerSectionLabel>
          <DrawerSectionValue>{description}</DrawerSectionValue>
        </div>
      </DrawerSection>

      {/* Bug 479697: Lifecycle Status before Metadata per Figma design order */}
      {/* Feature Collection Lifecycle Status Visual (PBI 150666) */}
      {/* Bug 538201: No permission gate — status is display-only; action buttons are
          internally gated by getCanActivateCollection / getCanObsoleteCollection */}
      <DrawerSection>
        <LifecycleStatusVisual
          status={currentStatus}
          collectionName={title}
          onActivateClick={handleActivateClick}
          onObsoleteClick={handleObsoleteClick}
          hasValidationWarning={hasValidationWarning}
        />
      </DrawerSection>

      {/* Metadata Section */}
      <DrawerSection>
        <div>
          <DrawerSectionLabel>
            Vehicle Type:
            <Tooltip
              title={
                strings.vehicle_type_tooltip ||
                'Ensure this type matches the vehicle to avoid unexpected Feature behavior.'
              }
            >
              <span style={{ marginLeft: 4, cursor: 'default' }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.20572 8.22795H7.45488V7.97811C7.45488 7.82413 7.47845 7.69057 7.52559 7.57744C7.57273 7.46431 7.65443 7.35746 7.77071 7.2569C7.88698 7.1532 8.04882 7.04164 8.25623 6.92222C8.58934 6.72738 8.84074 6.50269 9.01044 6.24815C9.18013 5.99046 9.26498 5.68406 9.26498 5.32896V5.31953C9.26498 4.96128 9.17071 4.64545 8.98215 4.37205C8.79675 4.09865 8.53591 3.88496 8.19966 3.73098C7.86655 3.57699 7.47531 3.5 7.02593 3.5C6.54512 3.5 6.13502 3.58328 5.79562 3.74983C5.45937 3.91639 5.20011 4.14893 5.01785 4.44747C4.83872 4.74287 4.74444 5.08227 4.73502 5.46566V5.47508L6.00774 5.47037L6.01717 5.46566C6.0266 5.28967 6.07059 5.13726 6.14916 5.00842C6.22772 4.87643 6.33457 4.7743 6.4697 4.70202C6.60483 4.62974 6.76195 4.5936 6.94108 4.5936C7.1202 4.5936 7.27733 4.62817 7.41246 4.69731C7.54759 4.7633 7.65129 4.85443 7.72357 4.97071C7.79899 5.08384 7.8367 5.21425 7.8367 5.36195V5.37138C7.8367 5.51908 7.81313 5.64792 7.76599 5.75791C7.71885 5.8679 7.63715 5.97318 7.52087 6.07374C7.40774 6.1743 7.24905 6.28586 7.04478 6.40842C6.83109 6.53412 6.65668 6.66925 6.52155 6.8138C6.38956 6.95836 6.29686 7.12334 6.24343 7.30875C6.19315 7.49102 6.17744 7.70628 6.1963 7.95455L6.20572 8.22795ZM6.8798 10.5C7.14377 10.5 7.35118 10.4293 7.50202 10.2879C7.656 10.1465 7.733 9.95477 7.733 9.71279C7.733 9.46453 7.656 9.27127 7.50202 9.133C7.35118 8.99158 7.14377 8.92088 6.8798 8.92088C6.61582 8.92088 6.40685 8.99158 6.25286 9.133C6.09888 9.27127 6.02189 9.46453 6.02189 9.71279C6.02189 9.95477 6.09888 10.1465 6.25286 10.2879C6.40685 10.4293 6.61582 10.5 6.8798 10.5Z"
                    fill="#0B2E6B"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13ZM7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                    fill="#8C96A2"
                  />
                </svg>
              </span>
            </Tooltip>
          </DrawerSectionLabel>
          <DrawerSectionValue>
            {getVehicleTypeDisplay(vehicleSubType)}
          </DrawerSectionValue>
        </div>
        <div>
          <DrawerSectionLabel>
            QuickFit Sales Code:
            <Tooltip
              title={
                strings.quickfit_sales_code_tooltip ||
                'Match this code to the vehicle to avoid unexpected Feature behavior.'
              }
            >
              <span style={{ marginLeft: 4, cursor: 'default' }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.20572 8.22795H7.45488V7.97811C7.45488 7.82413 7.47845 7.69057 7.52559 7.57744C7.57273 7.46431 7.65443 7.35746 7.77071 7.2569C7.88698 7.1532 8.04882 7.04164 8.25623 6.92222C8.58934 6.72738 8.84074 6.50269 9.01044 6.24815C9.18013 5.99046 9.26498 5.68406 9.26498 5.32896V5.31953C9.26498 4.96128 9.17071 4.64545 8.98215 4.37205C8.79675 4.09865 8.53591 3.88496 8.19966 3.73098C7.86655 3.57699 7.47531 3.5 7.02593 3.5C6.54512 3.5 6.13502 3.58328 5.79562 3.74983C5.45937 3.91639 5.20011 4.14893 5.01785 4.44747C4.83872 4.74287 4.74444 5.08227 4.73502 5.46566V5.47508L6.00774 5.47037L6.01717 5.46566C6.0266 5.28967 6.07059 5.13726 6.14916 5.00842C6.22772 4.87643 6.33457 4.7743 6.4697 4.70202C6.60483 4.62974 6.76195 4.5936 6.94108 4.5936C7.1202 4.5936 7.27733 4.62817 7.41246 4.69731C7.54759 4.7633 7.65129 4.85443 7.72357 4.97071C7.79899 5.08384 7.8367 5.21425 7.8367 5.36195V5.37138C7.8367 5.51908 7.81313 5.64792 7.76599 5.75791C7.71885 5.8679 7.63715 5.97318 7.52087 6.07374C7.40774 6.1743 7.24905 6.28586 7.04478 6.40842C6.83109 6.53412 6.65668 6.66925 6.52155 6.8138C6.38956 6.95836 6.29686 7.12334 6.24343 7.30875C6.19315 7.49102 6.17744 7.70628 6.1963 7.95455L6.20572 8.22795ZM6.8798 10.5C7.14377 10.5 7.35118 10.4293 7.50202 10.2879C7.656 10.1465 7.733 9.95477 7.733 9.71279C7.733 9.46453 7.656 9.27127 7.50202 9.133C7.35118 8.99158 7.14377 8.92088 6.8798 8.92088C6.61582 8.92088 6.40685 8.99158 6.25286 9.133C6.09888 9.27127 6.02189 9.46453 6.02189 9.71279C6.02189 9.95477 6.09888 10.1465 6.25286 10.2879C6.40685 10.4293 6.61582 10.5 6.8798 10.5Z"
                    fill="#0B2E6B"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13ZM7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z"
                    fill="#8C96A2"
                  />
                </svg>
              </span>
            </Tooltip>
          </DrawerSectionLabel>
          <DrawerSectionValue>{mdlDbCd || 'N/A'}</DrawerSectionValue>
        </div>
        <div>
          <DrawerSectionLabel>
            Minimum Diagnostic Variant(s):
          </DrawerSectionLabel>
          <DrawerSectionValue>
            {formatVarMinCompat(varMinCompat, collectionModuleNames)}
          </DrawerSectionValue>
        </div>
      </DrawerSection>
    </DrawerContentWrapper>
  )
}

export default FeatureCollectionDetailsDrawer
